import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import {
  extractRuleIdSet,
  pruneStaleRuleIdsFromInputs,
  reconcileDownstreamInputsOnKindChange,
  stripStableIdsFromInputs
} from '../helpers/draftGraphOperations';
import {
  applyDraftCache,
  optimisticDraftUpdate,
  restoreDraftCache
} from '../helpers/draftCacheHelpers';
import { appendPlaceholderPosition, buildPlaceholderStep } from '../helpers/placeholderStep';
import {
  reconcilePlaceholderInDraft,
  reconcilePlaceholderInLayout
} from '../helpers/reconcilePlaceholder';
import { expectSingleNode, reconcileEnvelope } from '../../draftGraph';
import { processLayoutKeys } from '../../query-keys';
import {
  type AddDraftStepRequest,
  type DeleteDraftStepsResponse,
  type DraftNodeEnvelope,
  type GetProcessDraftResponse,
  type GetProcessLayoutResponse,
  type UpdateDraftStepRequest
} from '@bliks-io/contracts';
import { basePath, mintTempDraftStepId } from './shared';

export function useAddDraftStep(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({ position, laneKey }: AddDraftStepRequest): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(`${basePath(processId)}/steps`, {
        position,
        ...(laneKey ? { laneKey } : {})
      });
      return data;
    },
    onMutate: async ({ position, laneKey }) => {
      const tempId = mintTempDraftStepId();
      const optimistic = await optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        draft: { ...old.draft, updatedAt: new Date() },
        steps: [
          ...old.steps,
          buildPlaceholderStep({
            tempId,
            processId,
            draftId: old.draft._id,
            laneKey
          })
        ]
      }));
      const layoutKey = processLayoutKeys.detail(processId, 'draft');
      const prevLayout = queryClient.getQueryData<GetProcessLayoutResponse>(layoutKey);
      if (prevLayout) {
        queryClient.setQueryData<GetProcessLayoutResponse>(
          layoutKey,
          appendPlaceholderPosition(prevLayout, tempId, position)
        );
      }
      return { ...optimistic, prevLayout, tempId };
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
      if (ctx?.prevLayout) {
        queryClient.setQueryData(processLayoutKeys.detail(processId, 'draft'), ctx.prevLayout);
      }
    },
    onSuccess: (envelope, _vars, ctx) => {
      const tempId = ctx?.tempId;
      if (!tempId) return;
      const created = expectSingleNode(envelope, 'step');
      applyDraftCache(queryClient, processId, (old) =>
        reconcilePlaceholderInDraft(old, tempId, created)
      );
      const layoutKey = processLayoutKeys.detail(processId, 'draft');
      queryClient.setQueryData<GetProcessLayoutResponse>(layoutKey, (prev) =>
        prev ? reconcilePlaceholderInLayout(prev, tempId, String(created.stableStepId)) : prev
      );
    }
  });
}

export function useUpdateDraftStep(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stepId,
      patch
    }: {
      stepId: string;
      patch: UpdateDraftStepRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.patch<DraftNodeEnvelope>(
        `${basePath(processId)}/steps/${stepId}`,
        patch
      );
      return data;
    },
    onMutate: async ({ stepId, patch }) => {
      return optimisticDraftUpdate(queryClient, processId, (old) => {
        const prevStep = old.steps.find((s) => s._id === stepId);
        const nextSteps = old.steps.map((s) =>
          s._id === stepId ? { ...s, ...(patch as object) } : s
        );
        // An `actionType` flip invalidates downstream refs to this step
        // (task-style edges can't bind to a rule; rule tags outlive the rules).
        // Mirror the backend `reconcileDownstreamOnKindChange` so the canvas
        // doesn't flash a phantom successor before the server envelope lands.
        const patchActionType = (patch as { actionType?: 'task' | 'decision' }).actionType;
        if (prevStep && patchActionType) {
          const sourceStable = String(prevStep.stableStepId);
          return {
            ...old,
            steps: nextSteps.map((s) =>
              s._id === stepId
                ? s
                : reconcileDownstreamInputsOnKindChange(s, sourceStable, patchActionType)
            ),
            draftAssignments: old.draftAssignments.map((a) =>
              reconcileDownstreamInputsOnKindChange(a, sourceStable, patchActionType)
            )
          };
        }
        // A rules patch can drop rules — downstream nodes' inputs still carry
        // the stale `ruleIds`. Without pruning them here, the flow builder
        // reattaches the edge to the first remaining rule's handle until the
        // post-settle refetch lands. Mirrors the backend prune in
        // `update-draft-step.service.ts` so the UI converges immediately.
        const patchRules = (patch as { rules?: unknown }).rules;
        if (prevStep && Array.isArray(patchRules)) {
          const prevRuleIdSet = extractRuleIdSet(prevStep.rules);
          const liveRuleIds = extractRuleIdSet(patchRules as Array<{ _id?: unknown }>);
          const hasRemovedRule = Array.from(prevRuleIdSet).some((id) => !liveRuleIds.has(id));
          if (hasRemovedRule) {
            const sourceStable = String(prevStep.stableStepId);
            return {
              ...old,
              steps: nextSteps.map((s) =>
                pruneStaleRuleIdsFromInputs(s, sourceStable, liveRuleIds)
              ),
              draftAssignments: old.draftAssignments.map((a) =>
                pruneStaleRuleIdsFromInputs(a, sourceStable, liveRuleIds)
              )
            };
          }
        }
        return { ...old, steps: nextSteps };
      });
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    // The envelope carries the patched step plus every cascade-touched doc
    // (downstream rule-reference prunes, recomputed successors) — write them
    // all so the optimistic mirror above is replaced by server truth.
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Optimistic mirror of the backend's delete cascade, shared by the single and
 * bulk delete: drop the selected steps and strip the dangling input refs
 * their `stableStepId`s leave on remaining steps AND draftAssignments, so the
 * flow doesn't momentarily render orphaned downstream nodes (amber-dashed)
 * between the optimistic update and the server refetch.
 */
function stripDeletedStepsFromDraft(
  old: GetProcessDraftResponse,
  stepIds: ReadonlySet<string>
): GetProcessDraftResponse {
  const deletedStables = new Set(
    old.steps.filter((s) => stepIds.has(s._id)).map((s) => String(s.stableStepId))
  );
  return {
    ...old,
    draft: { ...old.draft, updatedAt: new Date() },
    steps: old.steps
      .filter((s) => !stepIds.has(s._id))
      .map((s) => stripStableIdsFromInputs(s, deletedStables)),
    draftAssignments: old.draftAssignments.map((a) => stripStableIdsFromInputs(a, deletedStables))
  };
}

export function useDeleteDraftStep(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (stepId: string): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        `${basePath(processId)}/steps/${stepId}`
      );
      return data;
    },
    onMutate: async (stepId) => {
      return optimisticDraftUpdate(queryClient, processId, (old) =>
        stripDeletedStepsFromDraft(old, new Set([stepId]))
      );
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Batch companion to `useDeleteDraftStep`: the editor's multi-select delete
 * sends ONE request for the whole selection. One request per step (each with
 * its own settle-invalidation refetches) tripped the API rate limit on large
 * selections — deletes past the limit 429'd, leaving steps behind, and the
 * failed draft refetch surfaced as "Entwurf konnte nicht geladen werden".
 */
/** Request-side cap of `DeleteDraftStepsRequestSchema` — larger selections are chunked. */
const BULK_DELETE_MAX_IDS_PER_REQUEST = 500;

export function useDeleteDraftSteps(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    // Selections above the contract's 500-id cap are sent as sequential
    // chunks (each chunk's cascades must observe the previous chunk's
    // writes) and the envelopes merged for one onSuccess reconciliation.
    mutationFn: async (stepIds: string[]): Promise<DeleteDraftStepsResponse> => {
      const merged: DeleteDraftStepsResponse = {
        nodes: [],
        createdNodeIds: [],
        deletedNodeIds: [],
        notFoundStepIds: [],
        failedStepIds: []
      };
      for (let i = 0; i < stepIds.length; i += BULK_DELETE_MAX_IDS_PER_REQUEST) {
        const { data } = await apiClient.post<DeleteDraftStepsResponse>(
          `${basePath(processId)}/steps/bulk-delete`,
          { stepIds: stepIds.slice(i, i + BULK_DELETE_MAX_IDS_PER_REQUEST) }
        );
        // Concat is fine for the id lists, and for duplicate docs the later
        // (fresher) occurrence wins because `reconcileEnvelope` upserts in order.
        merged.nodes.push(...data.nodes);
        merged.createdNodeIds.push(...data.createdNodeIds);
        merged.deletedNodeIds.push(...data.deletedNodeIds);
        merged.notFoundStepIds.push(...data.notFoundStepIds);
        merged.failedStepIds.push(...data.failedStepIds);
      }
      // A later chunk can delete a node an earlier chunk reported as a
      // touched survivor. `reconcileEnvelope` applies ALL deletions before
      // upserting every node, so leaving that doc in `nodes` would resurrect
      // the deleted node from its stale copy. Mirror the backend's
      // per-request survivor filter across chunks.
      const deletedStableIds = new Set(merged.deletedNodeIds.map(String));
      merged.nodes = merged.nodes.filter((n) => !deletedStableIds.has(String(n.stableStepId)));
      return merged;
    },
    // Same optimistic strip as the single delete, applied for the whole batch
    // in one cache write.
    onMutate: async (stepIds) => {
      const idSet = new Set(stepIds);
      return optimisticDraftUpdate(queryClient, processId, (old) =>
        stripDeletedStepsFromDraft(old, idSet)
      );
    },
    onError: (_err, _vars, ctx) => {
      restoreDraftCache(queryClient, processId, ctx);
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/**
 * Convert a manual draft step into a draft assignment. Task content is stashed
 * server-side so a subsequent convert-back restores the user's in-progress
 * writing. The envelope reports the node under its NEW kind (assignment,
 * same `stableStepId`); the reconciler evicts it from `steps` and lands it in
 * `draftAssignments`.
 */
export function useConvertStepToAssignment(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({ stepId }: { stepId: string }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.post<DraftNodeEnvelope>(
        `${basePath(processId)}/steps/${stepId}/convert-to-assignment`
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
